// packages/console/src/components/PlayerOverlay.tsx
//
// In-browser preview for a completed job's encoded output. A center overlay with
// a <video> element, a quality selector, and a forced-download action.
//
// Playback modes:
//   * JOB MASTER (>=2 rungs for a segmented format): load ONE job-level master
//     manifest through ONE player that lives for the whole job, and switch
//     RENDITIONS on the SAME timeline (no manifest reload, no restart). The
//     <video> is NOT keyed on rung, so a quality change never tears it down.
//       - HLS master (master.m3u8) via one hls.js instance; the quality selector
//         flips hls.currentLevel.
//       - DASH master (master.mpd) / CMAF master (master.cmaf.mpd) via one dash.js
//         instance; the quality selector calls setRepresentationForTypeById on the
//         video type (or restores ABR). Every rung shares the segment grid, so the
//         switch lands on a shared boundary — seamless.
//     When more than one segmented format has a master, a format selector picks
//     which master is active (HLS preferred, then DASH, then CMAF); switching the
//     master format rebuilds that ONE player (still no per-rung reload within a
//     format).
//   * SINGLE RUNG (<2 rungs for the chosen format): each rung is an independent
//     single-rendition stream loaded from its per-rung deliver manifest;
//     switching rung rebuilds the player (today's behavior, kept exactly).
//
// Progressive outputs (MP4/WebM) play from a short-lived presigned object-store
// URL on a bare <video src>. Adaptive formats always go through the
// orchestrator's authenticated deliver proxy: a single bearer token authorises
// the manifest AND all its relative variant/segment requests (an hls.js xhrSetup
// / a dash.js request interceptor injects the Authorization header on every
// request). Native Safari HLS can't inject that header, so the authenticated
// proxy always goes through hls.js / dash.js.

import { useEffect, useMemo, useRef, useState } from "react";
import Hls from "hls.js";
import { MediaPlayer, type MediaPlayerClass } from "dashjs";
import { Icon } from "./Icons";
import { fetchOutputUrl, fetchDownloadAllUrl, type Container, type Job } from "../api/jobs";
import { servicePaths } from "../api/config";
import { getAccessToken } from "../api/session";

const j = servicePaths.jobs;

type Kind = "progressive" | "hls" | "dash";

// kindForFormat classifies a delivery format by how it is played inline:
//   * hls -> hls.js (fMP4 media playlist)
//   * dash and cmaf -> dash.js (cmaf is fMP4 segments over a DASH manifest, so
//     it plays through the exact same dash.js path as dash)
//   * mp4/mov/webm -> a bare <video src> from a presigned object URL
function kindForFormat(format: Container): Kind {
  if (format === "hls") return "hls";
  if (format === "dash" || format === "cmaf") return "dash";
  return "progressive";
}

// FORMAT_ORDER is the display order for the single-rung format selector: the
// directly-playable progressive files first, then the adaptive manifests. Only
// formats the rung ACTUALLY produced are offered (availableFormats), so we never
// present a format we cannot serve (R10).
const FORMAT_ORDER: Container[] = ["mp4", "webm", "mov", "hls", "dash", "cmaf"];

// FORMAT_LABEL is the human label for each delivery format in the selector.
const FORMAT_LABEL: Record<Container, string> = {
  mp4: "MP4",
  webm: "WebM",
  mov: "MOV",
  hls: "HLS",
  dash: "DASH",
  cmaf: "CMAF",
};

// manifestExtFor maps an adaptive format to the deliver-proxy manifest file
// extension for a rung: hls -> "<rung>.m3u8", dash -> "<rung>.mpd", cmaf ->
// "<rung>.cmaf.mpd" (matches the orchestrator's cmaf artifact name).
function manifestExtFor(format: Container): "m3u8" | "mpd" | "cmaf.mpd" {
  if (format === "hls") return "m3u8";
  if (format === "cmaf") return "cmaf.mpd";
  return "mpd";
}

// SEGMENTED_MASTER_FORMATS are the adaptive formats that get a JOB-LEVEL master
// manifest (>=2 capable rungs), preference-ordered: HLS is the primary ABR path,
// then DASH, then CMAF. A job may have masters for several of these at once.
const SEGMENTED_MASTER_FORMATS: Container[] = ["hls", "dash", "cmaf"];

// capableRungs returns the ready rung names (in the order given) that produced a
// given format. Availability is read from each output's `formats` list, so a
// rung is format-capable exactly when it has that format entry.
function capableRungs(job: Job, rungs: string[], format: Container): string[] {
  const capable = new Set(
    job.outputs
      .filter((o) => (o.formats ?? []).some((f) => f.format === format))
      .map((o) => o.name),
  );
  return rungs.filter((r) => capable.has(r));
}

// masterFormats returns the segmented formats that have a job-level master for
// this job: those with >=2 capable rungs, in HLS/DASH/CMAF preference order.
function masterFormats(job: Job, rungs: string[]): Container[] {
  return SEGMENTED_MASTER_FORMATS.filter((f) => capableRungs(job, rungs, f).length >= 2);
}

// masterFileFor maps a segmented master format to its job-root deliver filename:
// hls -> master.m3u8, dash -> master.mpd, cmaf -> master.cmaf.mpd (matches the
// orchestrator's uploadHLSMaster / uploadDASHMaster names).
function masterFileFor(format: Container): string {
  if (format === "hls") return "master.m3u8";
  if (format === "cmaf") return "master.cmaf.mpd";
  return "master.mpd";
}

// deliverManifestUrl builds the authenticated proxy URL for a single rung's
// manifest (the single-rung adaptive fallback for HLS, DASH and CMAF). The
// manifest is stored as "<rung>.<ext>" at the rung prefix; hls.js / dash.js
// resolve their relative sibling requests (segments/init) against this URL. The
// rung path segment is NOT URL-encoded past the manifest filename so a
// ".cmaf.mpd" extension (which contains a dot) resolves as a plain path suffix.
function deliverManifestUrl(jobId: string, rung: string, ext: "m3u8" | "mpd" | "cmaf.mpd"): string {
  return `${j}/v1/jobs/${encodeURIComponent(jobId)}/deliver/${encodeURIComponent(rung)}/${encodeURIComponent(rung)}.${ext}`;
}

// availableFormats returns the delivery formats a rung actually produced, in
// FORMAT_ORDER. Read from the rung's outputs.formats[], so we offer exactly what
// exists and each entry serves its OWN file (R10: never offer an absent format).
// This replaces the earlier primary-progressive-only stopgap: with format-aware
// presign, every produced format now resolves to the correct object.
function availableFormats(job: Job, rung: string): Container[] {
  const out = job.outputs.find((o) => o.name === rung);
  const produced = new Set((out?.formats ?? []).map((f) => f.format));
  return FORMAT_ORDER.filter((f) => produced.has(f));
}

// masterManifestUrl builds the authenticated proxy URL for the job-level master
// of a segmented format. Its relative refs ("<rung>/<rung>.m3u8" for HLS,
// "<rung>/<rung>_chunk_...m4s" for DASH/CMAF) resolve through the same flat
// deliver base. The master file basename contains dots for cmaf
// (master.cmaf.mpd), so it is appended as a plain path suffix (not encoded).
function masterManifestUrl(jobId: string, format: Container): string {
  return `${j}/v1/jobs/${encodeURIComponent(jobId)}/deliver/${masterFileFor(format)}`;
}

// authXhrSetup attaches the bearer token to the manifest request and to every
// relative variant/segment request, so the authenticated proxy serves the whole
// tree under one token.
function authXhrSetup(xhr: XMLHttpRequest) {
  const token = getAccessToken();
  if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
}

// LevelOpt is one entry in the master-mode quality selector (an index into
// hls.levels labelled by rendition height).
interface LevelOpt {
  index: number;
  label: string;
}

export function PlayerOverlay({
  job,
  rungs,
  onClose,
  onDownload,
}: {
  job: Job;
  rungs: string[];
  onClose: () => void;
  // onDownload saves one output. In single-rung mode it carries the selected
  // delivery format so the download serves THAT format's file; in master mode
  // (or when a rung produced only its primary) format is omitted and the primary
  // artifact is downloaded.
  onDownload: (rung: string, format?: Container) => void;
}) {
  // masterFmts are the segmented formats with a job-level master (>=2 capable
  // rungs), in HLS/DASH/CMAF preference order. Non-empty means we can serve a
  // master and switch renditions on one timeline; empty falls back to per-rung.
  const masterFmts = useMemo(() => masterFormats(job, rungs), [job, rungs]);
  const masterMode = masterFmts.length > 0;
  // masterFormat is the segmented format whose master is currently loaded. It
  // defaults to the most-preferred available (HLS) and is re-seeded if the job's
  // master set changes. Switching it rebuilds that ONE player (still no per-rung
  // reload within a format).
  const [masterFormat, setMasterFormat] = useState<Container>(masterFmts[0] ?? "hls");
  useEffect(() => {
    setMasterFormat((prev) => (masterFmts.includes(prev) ? prev : masterFmts[0] ?? "hls"));
  }, [masterFmts]);
  const masterIsHls = masterMode && masterFormat === "hls";
  const masterIsDash = masterMode && (masterFormat === "dash" || masterFormat === "cmaf");

  // rungsWithMaster are the capable rungs for the ACTIVE master format (used by
  // the download button's rung resolution).
  const rungsWithMaster = useMemo(
    () => (masterMode ? capableRungs(job, rungs, masterFormat) : []),
    [masterMode, job, rungs, masterFormat],
  );

  // --- master mode state ---
  // Selected rendition: -1 = Auto (ABR), else an index into the master's rendition
  // list. Options are populated once the manifest parses (hls.js MANIFEST_PARSED
  // or dash.js STREAM_INITIALIZED). The index space is shared by both players.
  const [levelIndex, setLevelIndex] = useState(-1);
  const [levelOpts, setLevelOpts] = useState<LevelOpt[]>([]);
  const hlsRef = useRef<Hls | null>(null);
  // levelRungsRef maps a rendition index -> rung name so the download button can
  // fetch the selected rendition's rung. For HLS it is resolved from each level's
  // media-playlist URL; for DASH/CMAF from each Representation id (the rung name).
  const levelRungsRef = useRef<string[]>([]);
  // dashMasterRepsRef maps a rendition index -> dash.js Representation id so a
  // pin can call setRepresentationForTypeById with the right id.
  const dashMasterRepsRef = useRef<string[]>([]);
  const dashMasterRef = useRef<MediaPlayerClass | null>(null);

  // --- single-rung fallback state ---
  const [rung, setRung] = useState(rungs[0] ?? "");
  // formats the selected rung produced, in display order (mp4/webm/mov/hls/
  // dash/cmaf). Each is individually playable and serves its own file.
  const formats = useMemo(() => availableFormats(job, rung), [job, rung]);
  // Selected delivery format for the current rung. Defaults to the first
  // available (progressive-preferred by FORMAT_ORDER) and is re-seeded whenever
  // the rung's produced set changes so it always names a real, produced format.
  const [format, setFormat] = useState<Container | null>(formats[0] ?? null);
  useEffect(() => {
    setFormat((prev) => (prev && formats.includes(prev) ? prev : formats[0] ?? null));
  }, [formats]);
  const [url, setUrl] = useState<string | null>(null);
  const dashRef = useRef<MediaPlayerClass | null>(null);

  const [err, setErr] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  // Preserve the playhead across single-rung rung/format switches. Each switch
  // remounts the <video> (keyed on rung/format/url), which would otherwise
  // restart from 0. captureResume records currentTime+play-state on the OLD
  // element BEFORE the state change; restorePosition reapplies it on the NEW
  // element's onLoadedMetadata. Master/HLS ABR mode switches on one timeline and
  // never uses this.
  const resumeRef = useRef<{ t: number; playing: boolean } | null>(null);
  const captureResume = () => {
    const v = videoRef.current;
    if (v && isFinite(v.currentTime)) resumeRef.current = { t: v.currentTime, playing: !v.paused };
  };
  const switchRung = (next: string) => {
    captureResume();
    setRung(next);
  };
  const switchFormat = (next: Container) => {
    captureResume();
    setErr(null); // a failed prior format must not strand the newly-selected one
    setFormat(next);
  };
  const restorePosition = () => {
    const v = videoRef.current;
    const r = resumeRef.current;
    if (!v || !r) return;
    const want = r.t > 0 && isFinite(r.t);
    if (want && Math.abs(v.currentTime - r.t) > 0.5) {
      try {
        v.currentTime = r.t;
      } catch {
        /* buffer not ready; re-attempted on the next canplay */
      }
    }
    if (r.playing) void v.play().catch(() => {});
    // Progressive seeks apply on loadedmetadata, so clear once at the target.
    // Adaptive players (hls.js/dash.js) append segments asynchronously, so the
    // seek can clamp to 0 on loadedmetadata; keep resumeRef and re-assert on the
    // media element's canplay (wired on the adaptive videos) until the buffer
    // covers the target.
    if (!want || Math.abs(v.currentTime - r.t) <= 0.5) resumeRef.current = null;
  };

  // HLS MASTER MODE: one hls.js instance for the whole job. Built on mount / job
  // change and destroyed only then (NOT on level change), so switching quality
  // never tears down the media element or restarts playback.
  useEffect(() => {
    if (!masterIsHls) return;
    const video = videoRef.current;
    if (!video) return;

    if (!Hls.isSupported()) {
      setErr("This browser can't play HLS inline. Use Download to play locally.");
      return;
    }
    setErr(null);

    const hls = new Hls({ xhrSetup: authXhrSetup });
    hlsRef.current = hls;

    hls.on(Hls.Events.MANIFEST_PARSED, () => {
      // hls.levels is ready here. Build the quality selector and the level->rung
      // map. Labels are the rendition height ("1080p"); the rung is derived from
      // the level's media-playlist URL ("<rung>/<rung>.m3u8").
      const opts: LevelOpt[] = hls.levels.map((lvl, i) => ({
        index: i,
        label: lvl.height ? `${lvl.height}p` : `Level ${i + 1}`,
      }));
      const map = hls.levels.map((lvl) => {
        const parts = (lvl.url?.[0] ?? "").split("?")[0].split("/").filter(Boolean);
        // Penultimate path segment is the rung dir (".../<rung>/<rung>.m3u8").
        return parts.length >= 2 ? decodeURIComponent(parts[parts.length - 2]) : "";
      });
      levelRungsRef.current = map;
      setLevelOpts(opts);
      setLevelIndex(-1); // start on Auto (ABR)
    });

    hls.on(Hls.Events.ERROR, (_evt, data) => {
      if (data.fatal) setErr("Couldn't play this HLS stream. Use Download to play locally.");
    });

    hls.loadSource(masterManifestUrl(job.id, "hls"));
    hls.attachMedia(video);

    return () => {
      hlsRef.current = null;
      levelRungsRef.current = [];
      setLevelOpts([]);
      hls.destroy();
    };
  }, [masterIsHls, job.id]);

  // Apply the selected level WITHOUT rebuilding hls.js. -1 restores ABR; a
  // concrete index pins the rendition on the same timeline.
  useEffect(() => {
    if (!masterIsHls) return;
    const hls = hlsRef.current;
    if (hls) hls.currentLevel = levelIndex;
  }, [masterIsHls, levelIndex]);

  // DASH/CMAF MASTER MODE: one dash.js instance for the whole job, loading the
  // single job-level master.mpd (resp. master.cmaf.mpd). Built on mount / job /
  // masterFormat change and destroyed only then (NOT on rendition change), so a
  // quality switch never tears down the media element or restarts playback. The
  // request interceptor injects the bearer token on the manifest AND every
  // segment/init request, so the authenticated deliver proxy serves the whole
  // tree under one token. Every rung shares the segment grid, so switching
  // Representations lands on a shared boundary — seamless.
  useEffect(() => {
    if (!masterIsDash) return;
    const video = videoRef.current;
    if (!video) return;
    setErr(null);

    const player = MediaPlayer().create();
    dashMasterRef.current = player;

    player.addRequestInterceptor(async (request) => {
      const token = getAccessToken();
      if (token) {
        request.headers = { ...(request.headers ?? {}), Authorization: `Bearer ${token}` };
      }
      return request;
    });

    const label = FORMAT_LABEL[masterFormat];
    player.on("error", () => {
      setErr(`Couldn't play this ${label} stream. Use Download to play locally.`);
    });

    // STREAM_INITIALIZED fires once the manifest is parsed and the video
    // representations are known. Build the quality selector, the index->rung map,
    // and the index->Representation-id map (dash.js switches by id). Reps are
    // ordered ascending by bitrate to match the HLS level ordering.
    player.on(MediaPlayer.events.STREAM_INITIALIZED, () => {
      const reps = player.getRepresentationsByType("video") ?? [];
      const sorted = [...reps].sort((a, b) => (a.bandwidth ?? 0) - (b.bandwidth ?? 0));
      const opts: LevelOpt[] = sorted.map((r, i) => ({
        index: i,
        label: r.height ? `${r.height}p` : `Level ${i + 1}`,
      }));
      // The Representation id IS the rung name (the master names each video
      // Representation id="<rung>"), so it doubles as the download rung.
      dashMasterRepsRef.current = sorted.map((r) => r.id);
      levelRungsRef.current = sorted.map((r) => r.id);
      setLevelOpts(opts);
      setLevelIndex(-1); // start on Auto (ABR)
    });

    // initialize(view, source, autoPlay).
    player.initialize(video, masterManifestUrl(job.id, masterFormat), true);

    return () => {
      dashMasterRef.current = null;
      dashMasterRepsRef.current = [];
      levelRungsRef.current = [];
      setLevelOpts([]);
      player.destroy();
    };
  }, [masterIsDash, job.id, masterFormat]);

  // Apply the selected rendition to the DASH/CMAF master WITHOUT rebuilding
  // dash.js. -1 restores ABR (autoSwitchBitrate on); a concrete index pins the
  // Representation by id on the same timeline (forceReplace so the switch takes
  // effect at the next segment, seamlessly).
  useEffect(() => {
    if (!masterIsDash) return;
    const player = dashMasterRef.current;
    if (!player) return;
    if (levelIndex < 0) {
      player.updateSettings({ streaming: { abr: { autoSwitchBitrate: { video: true } } } });
      return;
    }
    const id = dashMasterRepsRef.current[levelIndex];
    if (!id) return;
    player.updateSettings({ streaming: { abr: { autoSwitchBitrate: { video: false } } } });
    player.setRepresentationForTypeById("video", id, true);
  }, [masterIsDash, levelIndex]);

  // kind selects the inline player for the current single-rung selection from the
  // chosen delivery FORMAT (not the URL extension): progressive <video src>, hls.js,
  // or dash.js (which also serves cmaf). In master mode it follows the active
  // master format (hls -> hls.js, dash/cmaf -> dash.js).
  const kind: Kind | null = masterMode
    ? kindForFormat(masterFormat)
    : format
    ? kindForFormat(format)
    : null;

  // SINGLE-RUNG FALLBACK: for a PROGRESSIVE format, fetch the presigned object
  // URL for the selected rung AND FORMAT so the <video src> streams the right
  // file directly from object storage. Adaptive formats (hls/dash/cmaf) play
  // through the authenticated deliver proxy and need no presigned URL, so this
  // fetch is skipped for them. Skipped entirely in master mode.
  useEffect(() => {
    if (masterMode) return;
    let alive = true;
    setUrl(null);
    setErr(null);
    if (!rung || !format) return;
    if (kind !== "progressive") return;
    // On a rung switch the format reseed effect runs a tick later, so `format`
    // can momentarily name a format the NEW rung didn't produce. Skip the fetch
    // until the format is valid for this rung, to avoid a doomed presign request
    // (the reseed then re-triggers this effect with a produced format).
    if (!formats.includes(format)) return;
    fetchOutputUrl(job.id, rung, { format })
      .then((o) => alive && setUrl(o.url))
      .catch((e) => alive && setErr(e instanceof Error ? e.message : "could not load output"));
    return () => {
      alive = false;
    };
  }, [masterMode, job.id, rung, format, kind, formats]);

  // SINGLE-RUNG FALLBACK: wire hls.js for a single HLS rung. Keyed on rung so a
  // rung switch tears down and rebuilds the hls instance cleanly (today's
  // behavior). Skipped in master mode (handled by the effect above).
  useEffect(() => {
    if (masterMode) return;
    if (kind !== "hls") return;
    const video = videoRef.current;
    if (!video) return;

    if (!Hls.isSupported()) {
      setErr("This browser can't play HLS inline. Use Download to play locally.");
      return;
    }

    const hls = new Hls({ xhrSetup: authXhrSetup });
    hls.on(Hls.Events.ERROR, (_evt, data) => {
      if (data.fatal) setErr("Couldn't play this HLS stream. Use Download to play locally.");
    });
    hls.loadSource(deliverManifestUrl(job.id, rung, "m3u8"));
    hls.attachMedia(video);

    return () => {
      hls.destroy();
    };
  }, [masterMode, kind, job.id, rung, format]);

  // SINGLE-RUNG DASH/CMAF: wire dash.js for the selected rung's single-
  // representation MPD. CMAF is fMP4 segments carried by a DASH manifest, so it
  // plays through this exact path; only the manifest extension differs
  // (<rung>.mpd for dash, <rung>.cmaf.mpd for cmaf). Keyed on rung AND format so
  // switching either tears down and rebuilds the player cleanly (mirrors the HLS
  // fallback). A request interceptor injects the bearer token on the manifest AND
  // every segment/init request, so the authenticated deliver proxy serves the
  // whole tree under one token — the DASH analogue of the hls.js xhrSetup.
  // dash.js 5.x uses async request interceptors that return the (mutated)
  // request; we set request.headers.Authorization.
  useEffect(() => {
    if (masterMode) return;
    if (kind !== "dash" || !format) return;
    const video = videoRef.current;
    if (!video) return;

    setErr(null);
    const player = MediaPlayer().create();
    dashRef.current = player;

    player.addRequestInterceptor(async (request) => {
      const token = getAccessToken();
      if (token) {
        request.headers = { ...(request.headers ?? {}), Authorization: `Bearer ${token}` };
      }
      return request;
    });

    // dashjs.MediaPlayer.events.ERROR fires 'error' for fatal manifest/segment
    // failures; surface an honest message (the Download affordance remains).
    const label = FORMAT_LABEL[format];
    player.on("error", () => {
      setErr(`Couldn't play this ${label} stream. Use Download to play locally.`);
    });

    // initialize(view, source, autoPlay) — autoplay + native <video> controls,
    // consistent with the HLS path. The manifest ext is format-aware so cmaf
    // loads <rung>.cmaf.mpd and dash loads <rung>.mpd.
    player.initialize(video, deliverManifestUrl(job.id, rung, manifestExtFor(format)), true);

    return () => {
      dashRef.current = null;
      player.destroy();
    };
  }, [masterMode, kind, job.id, rung, format]);

  useEffect(() => {
    const h = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  // Download-all: only meaningful for a completed job (the whole stack exists).
  // On click we mint the short-lived signed URL and navigate the browser to it,
  // which starts the zip download (the token in the URL authenticates it).
  const [downloadingAll, setDownloadingAll] = useState(false);
  const canDownloadAll = job.state === "completed";
  async function onDownloadAll() {
    if (downloadingAll) return;
    setDownloadingAll(true);
    try {
      const { url } = await fetchDownloadAllUrl(job.id);
      window.location.assign(url);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "could not prepare the download");
    } finally {
      setDownloadingAll(false);
    }
  }

  const title = job.sourceFilename || (job.sourceObjectKey.split("/").pop() ?? job.sourceObjectKey);

  // downloadRung is the rung the Download button acts on: in master mode it's the
  // pinned rendition's rung, or the top rung on Auto; in fallback it's the
  // selected rung. rungs is highest-resolution first, so rungs[0] is the top rung.
  const downloadRung = masterMode
    ? (levelIndex >= 0 ? levelRungsRef.current[levelIndex] : "") || rungsWithMaster[0] || rungs[0] || ""
    : rung;
  // downloadFormat carries the currently-selected delivery format into the
  // Download action so it saves THAT format's file (format-aware presign). In
  // master mode we let the server pick the rung's primary (undefined).
  const downloadFormat = masterMode ? undefined : format ?? undefined;

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 60, display: "grid", placeItems: "center", background: "rgba(3,6,12,.72)", padding: 24 }}>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ width: "min(1000px, 94vw)", display: "flex", flexDirection: "column", background: "var(--bg-panel)", border: "1px solid var(--line-strong)", borderRadius: "var(--r-md)", boxShadow: "0 24px 80px rgba(0,0,0,.55)", overflow: "hidden" }}
      >
        {/* header */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", borderBottom: "1px solid var(--line)" }}>
          <span style={{ font: "400 12px var(--font-mono)", color: "#fff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{title}</span>
          <div style={{ flex: 1 }} />
          {masterMode ? (
            <>
              {/* Master-format selector: offered only when the job has masters for
                  more than one segmented format (HLS/DASH/CMAF). Switching it
                  rebuilds that ONE player; renditions still switch on one timeline
                  within the chosen format. */}
              {masterFmts.length > 1 && (
                <select
                  value={masterFormat}
                  onChange={(e) => setMasterFormat(e.target.value as Container)}
                  title="Streaming format"
                  style={{ minHeight: 32, padding: "0 8px", borderRadius: "var(--r-sm)", cursor: "pointer", font: "400 12px var(--font-mono)", color: "#fff", background: "var(--bg-elev, #171c24)", border: "1px solid var(--line-strong)" }}
                >
                  {masterFmts.map((fmt) => (
                    <option key={fmt} value={fmt}>
                      {FORMAT_LABEL[fmt]}
                    </option>
                  ))}
                </select>
              )}
              {/* Quality selector: Auto (ABR) + one option per rendition. Changing
                  it pins the rendition on the SAME timeline (no restart) — via
                  hls.currentLevel for HLS, setRepresentationForTypeById for
                  DASH/CMAF. */}
              {levelOpts.length > 0 && (
                <select
                  value={levelIndex}
                  onChange={(e) => setLevelIndex(Number(e.target.value))}
                  title="Quality"
                  style={{ minHeight: 32, padding: "0 8px", borderRadius: "var(--r-sm)", cursor: "pointer", font: "400 12px var(--font-mono)", color: "#fff", background: "var(--bg-elev, #171c24)", border: "1px solid var(--line-strong)" }}
                >
                  <option value={-1}>Auto</option>
                  {levelOpts.map((o) => (
                    <option key={o.index} value={o.index}>
                      {o.label}
                    </option>
                  ))}
                </select>
              )}
            </>
          ) : (
            <>
              {rungs.length > 1 && (
                <select
                  value={rung}
                  onChange={(e) => switchRung(e.target.value)}
                  title="Output rung"
                  style={{ minHeight: 32, padding: "0 8px", borderRadius: "var(--r-sm)", cursor: "pointer", font: "400 12px var(--font-mono)", color: "#fff", background: "var(--bg-elev, #171c24)", border: "1px solid var(--line-strong)" }}
                >
                  {rungs.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              )}
              {/* Format selector: offered only when the rung produced more than
                  one delivery format. Each option plays AND downloads its own
                  file (format-aware presign), so switching never serves the
                  wrong artifact. A single-format rung shows no selector. */}
              {formats.length > 1 && format && (
                <select
                  value={format}
                  onChange={(e) => switchFormat(e.target.value as Container)}
                  title="Delivery format"
                  style={{ minHeight: 32, padding: "0 8px", borderRadius: "var(--r-sm)", cursor: "pointer", font: "400 12px var(--font-mono)", color: "#fff", background: "var(--bg-elev, #171c24)", border: "1px solid var(--line-strong)" }}
                >
                  {formats.map((fmt) => (
                    <option key={fmt} value={fmt}>
                      {FORMAT_LABEL[fmt]}
                    </option>
                  ))}
                </select>
              )}
            </>
          )}
          <button
            className="ae-b"
            onClick={() => onDownload(downloadRung, downloadFormat)}
            title="Download this output"
            style={{ display: "inline-flex", alignItems: "center", gap: 6, minHeight: 32, padding: "0 12px", borderRadius: "var(--r-sm)", cursor: "pointer", font: "600 10px var(--font-sans)", letterSpacing: ".09em", textTransform: "uppercase", color: "var(--fg1)", background: "transparent", border: "1px solid var(--line-strong)" }}
          >
            <Icon name="upload" size={12} style={{ transform: "rotate(180deg)" }} />
            Download
          </button>
          {canDownloadAll && (
            // Download the ENTIRE output stack (every rung/format + master + poster)
            // as one zip (Martha #9). Meaningful only for a completed job.
            <button
              className="ae-b"
              onClick={onDownloadAll}
              disabled={downloadingAll}
              title="Download the whole output stack as a zip"
              style={{ display: "inline-flex", alignItems: "center", gap: 6, minHeight: 32, padding: "0 12px", borderRadius: "var(--r-sm)", cursor: downloadingAll ? "default" : "pointer", opacity: downloadingAll ? 0.6 : 1, font: "600 10px var(--font-sans)", letterSpacing: ".09em", textTransform: "uppercase", color: "var(--fg1)", background: "transparent", border: "1px solid var(--line-strong)" }}
            >
              <Icon name="upload" size={12} style={{ transform: "rotate(180deg)" }} />
              {downloadingAll ? "Preparing…" : "Download all"}
            </button>
          )}
          <button
            className="ae-b"
            onClick={onClose}
            title="Close (Esc)"
            style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 30, height: 30, borderRadius: "var(--r-sm)", background: "transparent", border: "1px solid var(--line-strong)", cursor: "pointer", color: "var(--fg2)" }}
          >
            <Icon name="x" size={14} />
          </button>
        </div>

        {/* body */}
        <div style={{ background: "#000", display: "grid", placeItems: "center", minHeight: 320 }}>
          {err ? (
            <div style={{ padding: 40, color: "var(--err)", font: "var(--t-body-sm)" }}>{err}</div>
          ) : masterMode ? (
            // ONE <video> for the whole job in the active master format (NOT keyed
            // on rung/rendition), so a quality change never tears it down. hls.js
            // (HLS master) or dash.js (DASH/CMAF master) attaches via the master
            // effect above. Keyed on the master FORMAT so switching HLS<->DASH
            // hands the element cleanly from one player to the other.
            <video key={`master:${job.id}:${masterFormat}`} ref={videoRef} onLoadedMetadata={restorePosition} onCanPlay={restorePosition} controls autoPlay style={{ width: "100%", maxHeight: "72vh", display: "block", background: "#000" }} />
          ) : kind === "dash" ? (
            // Single DASH/CMAF rung: dash.js attaches via the DASH effect above
            // (keyed on rung+format so a rung/format switch rebuilds the player
            // cleanly). Adaptive playback goes through the deliver proxy, so this
            // needs no presigned url.
            <video key={`dash:${rung}:${format}`} ref={videoRef} onLoadedMetadata={restorePosition} onCanPlay={restorePosition} controls autoPlay style={{ width: "100%", maxHeight: "72vh", display: "block", background: "#000" }} />
          ) : kind === "hls" ? (
            // Single HLS rung: hls.js attaches via the fallback effect above
            // (keyed on rung so a rung switch rebuilds the instance cleanly).
            <video key={`hls:${rung}`} ref={videoRef} onLoadedMetadata={restorePosition} onCanPlay={restorePosition} controls autoPlay style={{ width: "100%", maxHeight: "72vh", display: "block", background: "#000" }} />
          ) : !url ? (
            <div style={{ padding: 40, color: "var(--fg4)", font: "var(--t-micro)" }}>Loading preview…</div>
          ) : (
            <video key={url} ref={videoRef} src={url} onLoadedMetadata={restorePosition} controls autoPlay style={{ width: "100%", maxHeight: "72vh", display: "block", background: "#000" }} />
          )}
        </div>
      </div>
    </div>
  );
}
